import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, CheckCircle, AlertCircle, ChevronDown, ChevronUp, Edit, ClipboardCheck, X, MapPin, Calendar, Clock, Lock, AlertTriangle, BookOpen, UserX, Phone, MessageSquare, Mail, ArrowRight, UserCircle2 } from "lucide-react";

import NationalPhotosPanel from "./NationalPhotosPanel";
import OrderMapSection from "./OrderMapSection";
// Angela 2026-05-10 — ClientPhotoRequestPanel removed from Scheduling order
// detail per user scope lock. Component file is preserved on disk but no
// longer imported/rendered here.
import ExternalMorningCallSection from "./ExternalMorningCallSection";
import { useAuth } from "./App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISPATCH_STATUSES = new Set(["Assigned", "In Field", "Hold", "Ready to Route", "Ready for Dispatch"]);

// Scheduling-owned check (Angela 2026-04-29):
// Mirrors the backend `is_scheduling_owned()` rule.  An order is OUT of
// Scheduling (i.e. legitimately in Dispatch) when ANY of:
//   - flow_type !== "Internal"               (External was never Scheduling-owned)
//   - appointment_set === "yes"              (Rule A — appointment confirmed)
//   - flipped_to_external === true AND ≥1 call logged (Rule B)
// While Scheduling-owned, NO Dispatch badge/button must appear.
const _isSchedulingOwnedClient = (order) => {
  if (!order) return false;
  const flow = String(order.flow_type || "").toLowerCase();
  if (flow !== "internal") return false;
  const archived = String(order.archive_status || "").toLowerCase() === "archived";
  if (archived) return false;
  const apptSet = String(order.appointment_set || "").toLowerCase() === "yes";
  if (apptSet) return false;
  if (order.flipped_to_external === true) {
    const calls = (order.call_count || 0) +
                  (order.agent_call_count || 0) +
                  (order.attempts_number || 0);
    return calls === 0;  // flipped but no call yet → still Scheduling-owned
  }
  return true;
};

const SendToDispatchButton = ({ order, onSuccess, onError }) => {
  const [sending, setSending] = useState(false);
  const alreadyInDispatch = DISPATCH_STATUSES.has(order?.status);
  // FAKE-DISPATCH-BADGE FIX (Angela 2026-04-29):
  // If the order is still Scheduling-owned per the canonical rule, NEVER
  // show "Already in Dispatch" — a legacy `status=Assigned` value on an
  // unappointed Internal order is leftover data, not a real dispatch state.
  // We also do not show the Send button (the order isn't legally eligible
  // yet — operator must complete appointment or call-after-flip first).
  const stillSchedulingOwned = _isSchedulingOwnedClient(order);
  if (stillSchedulingOwned) return null;

  const handleSend = async () => {
    if (alreadyInDispatch || sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/orders/${order.id}/send-to-dispatch`);
      onSuccess(res.data);
    } catch (e) {
      onError(e.response?.data?.detail || "Failed to send to Dispatch");
    } finally {
      setSending(false);
    }
  };

  if (alreadyInDispatch) {
    return (
      <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 rounded-lg text-sm text-gray-500 border border-gray-200" data-testid="already-in-dispatch">
        <CheckCircle className="h-4 w-4 text-[#6B8F62]" />
        <span className="font-medium">Already in Dispatch</span>
        <span className="text-xs text-gray-400">(status: {order.status})</span>
      </div>
    );
  }

  return (
    <button
      onClick={handleSend}
      disabled={sending}
      className="w-full py-2.5 bg-[#2F5D8A] hover:bg-[#254A6F] text-white font-semibold rounded-lg disabled:opacity-50 transition-colors text-sm flex items-center justify-center gap-2"
      data-testid="send-to-dispatch-btn"
    >
      <ArrowRight className="h-4 w-4" />
      {sending ? "Sending..." : "Send to Dispatch"}
    </button>
  );
};

const formatDateDisplay = (d) => { if (!d) return '-'; const m = d.match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? `${m[2]}/${m[3]}/${m[1]}` : d; };

// ─── PHASE 0 (Angela 2026-04-29) ────────────────────────────────────
// AARIFI Internal windows (REAL) → Safeguard windows (REPORTED, shifted).
// These are the ONLY six legal windows.  Operator picks Internal from
// the dropdown; Safeguard is auto-displayed from this table.
const WINDOW_CONFIG = {
  '8:00-9:00':   { start: '08:00', end: '09:00' },
  '8:00-12:00':  { start: '08:00', end: '12:00' },   // Angela 2026-04-29: confirmed valid
  '9:00-1:00':   { start: '09:00', end: '13:00' },
  '9:30-1:30':   { start: '09:30', end: '13:30' },
  '10:00-2:00':  { start: '10:00', end: '14:00' },
  '11:00-3:00':  { start: '11:00', end: '15:00' },
};
// Internal label → Safeguard label (shown to operator + sent to Safeguard).
const INTERNAL_TO_SG_LABEL = {
  '8:00-9:00':   '9:00-11:00',
  '8:00-12:00':  '12:00-4:00',   // Angela 2026-04-29
  '9:00-1:00':   '1:00-5:00',
  '9:30-1:30':   '1:30-5:30',
  '10:00-2:00':  '2:00-6:00',
  '11:00-3:00':  '3:00-7:00',
};
const REAL_TIME_TO_SG_TIME = INTERNAL_TO_SG_LABEL;
// 2026-07-30 (Angela): only these 3 internal windows are selectable now.
// Legacy orders with 8:00-9:00 / 9:30-1:30 / 10:00-2:00 still display via
// WINDOW_CONFIG + INTERNAL_TO_SG_LABEL above, but cannot be re-selected.
const REAL_TIME_OPTIONS = ['8:00-12:00', '9:00-1:00', '11:00-3:00'];

// PHASE 0 (Angela 2026-04-29): operator-facing display MUST be AM/PM.
// SG labels in DB are stored as 12h-implicit ("1:00-5:00") so a generic
// 24h parser would mis-read "2:00-6:00" as 2 AM-6 AM.  Use explicit
// table so the AM/PM is correct for every legal Safeguard window.
const SG_LABEL_TO_AMPM = {
  '9:00-11:00': '9:00 AM – 11:00 AM',
  '12:00-4:00': '12:00 PM – 4:00 PM',  // Angela 2026-04-29
  '1:00-5:00':  '1:00 PM – 5:00 PM',
  '1:30-5:30':  '1:30 PM – 5:30 PM',
  '2:00-6:00':  '2:00 PM – 6:00 PM',
  '3:00-7:00':  '3:00 PM – 7:00 PM',
};
function formatSgTime(sgRaw) {
  if (!sgRaw) return '';
  const k = String(sgRaw).trim();
  if (SG_LABEL_TO_AMPM[k]) return SG_LABEL_TO_AMPM[k];
  // Legacy / unknown label — best effort, but mark uppercase to surface oddity.
  return k;
}
function _toAmPm(t) {
  if (!t) return '';
  const m = String(t).trim().match(/^(\d{1,2})(?::(\d{1,2}))?\s*(AM|PM)?$/i);
  if (!m) return String(t);
  let h = parseInt(m[1], 10);
  const min = (m[2] || '00').padStart(2, '0');
  const explicit = (m[3] || '').toUpperCase();
  if (explicit) return `${h}:${min} ${explicit}`;
  // No AM/PM → assume 24h (used for internal_start_time/internal_end_time only).
  const ampm = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${min} ${ampm}`;
}
function formatInternalRange(start24, end24) {
  if (!start24 || !end24) return '';
  return `${_toAmPm(start24)} – ${_toAmPm(end24)}`;
}

// ── Availability lines helper (Angela 2026-02) ──────────────────────
// Reusable: pulls the next-N AARIFI INTERNAL windows from the same
// `zoneInfo` already loaded by `fetchZoneScheduling()` (which calls
// /api/zones/{zone_id}/next-dates-capacity). Returns up to `max` pretty
// lines like "Thursday, May 7 — 9:00 AM to 1:00 PM" picking the first
// non-full window of each upcoming date. Returns [] when zoneInfo is
// missing/empty or every upcoming window is full. Designed to be reused
// by future email follow-up templates without further refactor.
const buildAvailabilityLines = (zoneInfoLocal, max = 2) => {
  const out = [];
  if (!zoneInfoLocal || !Array.isArray(zoneInfoLocal.dates)) return out;
  for (const d of zoneInfoLocal.dates) {
    if (out.length >= max) break;
    if (!d || !d.date || !Array.isArray(d.windows)) continue;
    const firstOpen = d.windows.find(w => w && !w.full);
    if (!firstOpen || !firstOpen.window) continue;
    let dateStr = d.date;
    try {
      const dt = new Date(d.date + 'T00:00:00');
      dateStr = dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    } catch (_e) { /* keep ISO fallback */ }
    const timeStr = toAmPm(firstOpen.window).replace(/\s*-\s*/, ' to ');
    out.push(`${dateStr} — ${timeStr}`);
  }
  return out;
};

// Convert an internal time/range (e.g. "13:00", "09:00 - 13:00", "9:00-1:00") to US AM/PM.
// Inspection windows: hours 7-11 treated as AM, 1-6 as PM, 12 as noon-PM, 13+ subtract 12.
const _hourToAmPm = (t) => {
  if (!t) return t;
  const s = String(t).trim().toUpperCase();
  if (s.includes("AM") || s.includes("PM")) return s.replace(/^0(\d)/, "$1");
  const m = s.match(/^(\d{1,2}):?(\d{0,2})$/);
  if (!m) return t;
  const h = parseInt(m[1], 10);
  const mm = (m[2] || "00").padStart(2, "0");
  if (h === 0) return `12:${mm} AM`;
  if (h === 12) return `12:${mm} PM`;
  if (h > 12) return `${h - 12}:${mm} PM`;
  if (h >= 1 && h <= 6) return `${h}:${mm} PM`; // inspection afternoon
  return `${h}:${mm} AM`;
};
const toAmPm = (timeStr) => {
  if (!timeStr) return timeStr || "";
  const s = String(timeStr).trim();
  if (s.includes("-")) {
    const parts = s.split("-").map(p => p.trim());
    return `${_hourToAmPm(parts[0])} - ${_hourToAmPm(parts[1])}`;
  }
  return _hourToAmPm(s);
};



// Activity dropdown — cleaned up (Apr 2026): removed "Active Call" and
// "End of Day Update" (no longer part of the workflow). "SMS" stays —
// SMS and Calls are logged as separate activities for productivity,
// contact-attempt tracking, and communication counts. Order below is the
// canonical order the Scheduling team uses daily.
const ACTIVITY_TYPES = [
  "Call PH", "Call Agent", "Incoming Call", "SMS", "Email",
  "Respond Safeguard Email", "Inspector Help", "Voicemail",
  "SMS RingCentral", "Voicemail RingCentral",
  "Set Appointment", "Priority", "Send to Admin Review",
];
const RESULTS = [
  "No Answer", "Left Voicemail", "Spoke with PH", "Spoke with Agent",
  "Incoming Call Received", "Sent SMS", "Sent Email", "Appointment Set",
  "Wrong Number", "Disconnected", "Denied Inspection",
  "Other",
];

const EXTERNAL_ACTIVITIES = ["Call PH", "Call Agent", "Incoming Call", "SMS", "Email", "Respond Safeguard Email", "Inspector Help", "Voicemail", "SMS RingCentral", "Voicemail RingCentral", "Priority", "Send to Admin Review"];

// ── Helpers ──
// FINAL INSPECTOR RULE (Angela 2026-04-29):
//   A real inspector is ONLY someone registered as an active inspector on
//   the Inspector page. The frontend pulls the live roster from the backend
//   (`/api/inspectors/active-roster`) — no hardcoded list. Hard-known
//   placeholders are still rejected as a safety net.
const HARDCODED_PLACEHOLDERS = new Set([
  "", "AARIFI-SCHEDULING", "AARIFI-MANAGEMENT", "AARIFI-HOLD",
  "AARIFI-EXTERIOR", "AARIFI-DISPATCH", "UNASSIGNED",
]);
const isRealInspector = (name, roster) => {
  if (name == null) return false;
  const n = String(name).trim();
  if (!n) return false;
  if (HARDCODED_PLACEHOLDERS.has(n.toUpperCase())) return false;
  if (!roster) return false; // fail-closed without roster
  return roster.has(n.toUpperCase());
};

// SCHEDULING STATUS RULES (Angela 2026-04-29):
//   The Order Detail status shown to Scheduling MUST come from THIS derivation,
//   not the raw order.status field. Dispatch/QA/Finance statuses are NOT shown
//   here — Scheduling owns ONLY these labels:
//     INTERNAL: NEW | RETURNED FROM SAFEGUARD | IN PROGRESS | APPOINTMENT SET | FLIPPED TO EXTERIOR
//     EXTERNAL: NEW | RETURNED FROM SAFEGUARD | FLIPPED TO EXTERIOR ONLY
const computeSchedulingStatus = (o) => {
  if (!o) return 'NEW';
  const flow = String(o.flow_type || '').toLowerCase();
  const isExternal = flow === 'external';
  if (o.flipped_to_external === true) {
    return isExternal ? 'FLIPPED TO EXTERIOR ONLY' : 'FLIPPED TO EXTERIOR';
  }
  if (!isExternal && o.appointment_set === 'yes') {
    return 'APPOINTMENT SET';
  }
  if (o.returned === true && o.returned_destination === 'scheduling') {
    return 'RETURNED FROM SAFEGUARD';
  }
  if (!isExternal) {
    const hasActivity =
      (o.call_count || 0) > 0 ||
      (o.attempts_number || 0) > 0 ||
      (o.sms_count || 0) > 0 ||
      (o.agent_call_count || 0) > 0;
    if (hasActivity) return 'IN PROGRESS';
  }
  return 'NEW';
};
const schedulingStatusAccent = (label) => {
  switch (label) {
    case 'APPOINTMENT SET': return 'text-[#6B8F62]';
    case 'IN PROGRESS': return 'text-[#B08A3E]';
    case 'RETURNED FROM SAFEGUARD': return 'text-[#9A7A3E]';
    case 'FLIPPED TO EXTERIOR':
    case 'FLIPPED TO EXTERIOR ONLY': return 'text-[#6B5B8D]';
    default: return 'text-[#2F5D8A]';
  }
};

// F3-pré (Angela 2026-05-02) — AARIFI STATUS badge styling for the 5
// canonical scheduling_status values returned by backend F2
// (field: order.aarifi_scheduling_status). 100% read-only display.
// Design frozen 2026-05-02: label "AARIFI STATUS"; inside Order Summary
// grid beside the legacy Status; no cards/filters/queues affected.
const AARIFI_STATUS_STYLE = {
  "New Assignment":       "text-[#2F5D8A] font-bold",
  "In Progress":          "text-[#B08A3E] bg-amber-50 font-bold px-2 py-0.5 rounded",
  "Appointment Set":      "text-[#6B8F62] bg-green-50 font-bold px-2 py-0.5 rounded",
  "Denied":               "text-[#B91C1C] bg-red-50 font-bold px-2 py-0.5 rounded",
  "Removed by Safeguard": "text-gray-500 bg-gray-100 font-semibold italic px-2 py-0.5 rounded",
};
const aarifiStatusClass = (value) => AARIFI_STATUS_STYLE[value] || "text-gray-400 italic text-xs";


const SummaryField = ({ label, value, accent }) => (
  <div>
    <p className="text-gray-400 text-[10px] uppercase tracking-wider">{label}</p>
    <p className={`text-sm font-medium ${accent || 'text-[#1F2937]'}`}>{value != null && value !== '' ? String(value) : '-'}</p>
  </div>
);

const SectionTitle = ({ children }) => (
  <h2 className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-3">{children}</h2>
);

const selectCls = "w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#D1A054]/70 focus:border-[#D1A054]/70 transition-colors appearance-none";
const inputCls = "w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#D1A054]/70 focus:border-[#D1A054]/70 transition-colors";
const labelCls = "block text-gray-500 text-[10px] uppercase tracking-wider mb-1";

// Format date for display: "Apr 9" style
const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};


/* B13.A (Angela 2026-05-09) — Cancel Appointment modal REMOVED.
   AARIFI does not own rescheduling/cancellation as an app workflow.
   Safeguard/CIFG handles real cancellations; we passively re-import the
   change. Internal communication flow is intentionally NOT replaced here. */


// ── Main Component ──
const SchedulingOrderDetailPage = ({ Layout }) => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // RC Inbox context (set when navigating from RC SMS/Voicemail Inbox).
  // Snapshot it once into a ref so it survives re-renders without retriggering effects.
  const rcInboxCtx = location?.state?.rcInboxContext || null;
  const { user: authUser } = useAuth();
  const userRole = authUser?.role;

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  // Contact editing
  const [contacts, setContacts] = useState({});
  const [comments, setComments] = useState("");
  const [saving, setSaving] = useState(false);

  // Activity form
  const [actType, setActType] = useState("");
  const [actResult, setActResult] = useState("");
  const [actComment, setActComment] = useState("");
  // NOTE (Apr 2026): operator selector removed — operator is auto-resolved
  // from the authenticated user on the backend.
  const [submittingActivity, setSubmittingActivity] = useState(false);

  // Appointment sub-form
  const [apptRealDate, setApptRealDate] = useState("");
  const [apptRealTime, setApptRealTime] = useState("");
  const [apptSgDate, setApptSgDate] = useState("");
  const [apptSgTime, setApptSgTime] = useState("");
  const [apptContactName, setApptContactName] = useState("");
  const [apptContactPhone, setApptContactPhone] = useState("");

  // Call & SMS
  const [callLoading, setCallLoading] = useState(false);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsText, setSmsText] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [manualPhoneInput, setManualPhoneInput] = useState('');
  const [showManualPhone, setShowManualPhone] = useState(false);

  // History
  const [activityLog, setActivityLog] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Angela 2026-05-10 — Inspector Field Updates collapsible (separate from
  // Scheduling History so the main table stays clean for normal scheduling
  // activities). Loaded from /orders/{id}/inspector-field-events.
  const [fieldUpdates, setFieldUpdates] = useState([]);
  const [fieldUpdatesOpen, setFieldUpdatesOpen] = useState(false);

  // Activity modal
  const [showActivityModal, setShowActivityModal] = useState(false);
  // B13.A (Angela 2026-05-09): Cancel Appointment workflow REMOVED.

  // Post-Call flow: Call PH or Set Appointment → ask SMS → submit
  const [showPostCallSms, setShowPostCallSms] = useState(false);
  const [postCallStep, setPostCallStep] = useState("ask_sms"); // "ask_sms" | "sms_skip_reason"
  const [smsSkipReason, setSmsSkipReason] = useState("");
  const [pendingActivityPayload, setPendingActivityPayload] = useState(null);
  // Decisions accumulated across steps
  const [comboSmsSent, setComboSmsSent] = useState(false);
  const [comboSmsSkipReason, setComboSmsSkipReason] = useState("");
  // Email also-sent checkbox in the post-call combo modal (Apr 2026).
  // One registration, multiple counts: Call PH + optional SMS + optional Email.
  const [comboEmailSent, setComboEmailSent] = useState(false);
  // Override phone for SMS (operator can type a different number than the one on file)
  const [smsOverridePhone, setSmsOverridePhone] = useState("");

  // Zone scheduling info
  const [zoneInfo, setZoneInfo] = useState(null); // { zone_id, dates: [{date, windows: [...]}] }
  const [loadingZone, setLoadingZone] = useState(false);
  const [zoneNotMapped, setZoneNotMapped] = useState(false);

  // Training / Company rules
  const [training, setTraining] = useState(null);
  const [trainingOpen, setTrainingOpen] = useState(false);

  // FINAL INSPECTOR RULE (Angela 2026-04-29): roster from Inspector page.
  // Drives the placeholder display in this page. Re-fetched on mount + every
  // 60s so adding/removing an inspector reflects without a full reload.
  const [inspectorRoster, setInspectorRoster] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/inspectors/active-roster`);
        if (!cancelled) {
          setInspectorRoster(new Set((r.data?.roster || []).map((s) => String(s).toUpperCase())));
        }
      } catch (e) {
        // Fail-closed: leave roster null so isRealInspector returns false.
        if (!cancelled) setInspectorRoster(new Set());
      }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const fetchOrder = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}`);
      setOrder(res.data);
      setContacts({
        ph_name: res.data.ph_name || "", ph_phone: res.data.ph_phone || "", ph_email: res.data.ph_email || "",
        agt_name: res.data.agt_name || "", agt_phone: res.data.agt_phone || "", agt_email: res.data.agt_email || "",
      });
      setComments(res.data.comments || "");
      return res.data;
    } catch (err) {
      console.error("Failed to fetch order:", err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  const fetchLog = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}/activity-log`);
      setActivityLog(res.data);
    } catch (err) {
      console.error("Failed to fetch log:", err);
    }
  }, [orderId]);

  const fetchFieldUpdates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}/inspector-field-events`);
      setFieldUpdates(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch inspector field updates:", err);
    }
  }, [orderId]);

  // Operator list fetch removed (Apr 2026): no more manual Operator selector;
  // backend auto-resolves the operator from the authenticated user.
  const getOrderPhone = useCallback(() => {
    // STRICT PH-phone rule: manual override first, then original PH phone.
    // STRICT US 10-digit validation (Angela 2026-05-07): RingCentral RingOut
    // requires a complete US number. Anything shorter is rejected so the
    // operator never wastes a click on a malformed number.
    const tryNormalize = (raw) => {
      if (!raw) return null;
      const first = raw.toString().split(/[\s,;\/]+/).find(p => p.replace(/[^0-9]/g, '').length >= 10) || '';
      const digits = first.replace(/[^0-9]/g, '');
      if (digits.length === 10) return { formatted: `+1${digits}`, display: raw };
      if (digits.length === 11 && digits.startsWith('1')) return { formatted: `+${digits}`, display: raw };
      return null;
    };
    return tryNormalize(order?.ph_phone_manual)
        || tryNormalize(order?.ph_phone)
        || tryNormalize(manualPhoneInput)
        || null;
  }, [order, manualPhoneInput]);

  const handleCallClient = async () => {
    const phone = getOrderPhone();
    if (!phone) { setShowManualPhone(true); return; }
    if (!window.confirm(`Call ${phone.display}?\n\nRingCentral will ring your phone first, then connect to the client.`)) return;
    setCallLoading(true);
    try {
      await axios.post(`${API}/ringcentral/ring-out`, { to_number: phone.formatted, order_id: orderId });
      setMessage({ type: 'success', text: `Call initiated to ${phone.display}. Check your phone.` });
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Call failed' });
    }
    setCallLoading(false);
  };

  const handleSendOrderSms = async () => {
    const phone = getOrderPhone();
    if (!phone) { setShowManualPhone(true); return; }
    if (!smsText.trim()) { setMessage({ type: 'error', text: 'Type a message before sending.' }); return; }
    setSmsSending(true);
    try {
      await axios.post(`${API}/ringcentral/order-sms`, { to_number: phone.formatted, text: smsText.trim(), order_id: orderId });
      setMessage({ type: 'success', text: `SMS sent to ${phone.display}` });
      setShowSmsModal(false);
      setSmsText('');
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'SMS failed' });
    }
    setSmsSending(false);
  };

  // Fetch zone scheduling info for Internal orders
  const fetchZoneScheduling = useCallback(async (orderData) => {
    if (!orderData || orderData.flow_type !== "Internal") return;

    setLoadingZone(true);
    setZoneNotMapped(false);
    let zoneId = orderData.zone_id;

    // If no zone_id, try auto-matching by city
    if (!zoneId && orderData.city) {
      try {
        const lookup = await axios.get(`${API}/zones/city-lookup?city=${encodeURIComponent(orderData.city)}`);
        if (lookup.data.found) {
          zoneId = lookup.data.zone_id;
          // Auto-set zone on the order
          await axios.put(`${API}/orders/${orderId}`, { zone_id: zoneId });
          setOrder(prev => prev ? { ...prev, zone_id: zoneId } : prev);
        } else {
          setZoneNotMapped(true);
          setLoadingZone(false);
          return;
        }
      } catch (err) {
        console.error("City lookup failed:", err);
        setZoneNotMapped(true);
        setLoadingZone(false);
        return;
      }
    }

    if (!zoneId) {
      setZoneNotMapped(true);
      setLoadingZone(false);
      return;
    }

    // Fetch next 2 dates with capacity
    try {
      const res = await axios.get(`${API}/zones/${encodeURIComponent(zoneId)}/next-dates-capacity?limit=2`);
      setZoneInfo(res.data);
    } catch (err) {
      console.error("Failed to fetch zone dates:", err);
    } finally {
      setLoadingZone(false);
    }
  }, [orderId]);

  useEffect(() => {
    const init = async () => {
      const orderData = await fetchOrder();
      fetchLog();
      fetchFieldUpdates();
      if (orderData) {
        fetchZoneScheduling(orderData);
        // Fetch training by cliente (insurance company name)
        if (orderData.cliente) {
          try {
            const res = await axios.get(`${API}/training/by-company/${encodeURIComponent(orderData.cliente)}`);
            setTraining(res.data.found ? res.data : null);
          } catch (err) {
            console.error("Training fetch failed:", err);
          }
        }
      }
    };
    init();
  }, [fetchOrder, fetchLog, fetchZoneScheduling]);

  // RC Inbox handoff: when the user opens this order from the RingCentral
  // SMS/Voicemail inbox, pre-fill the activity form fields so when they
  // manually click "Register Activity" the type and reference are ready.
  // Important: we DO NOT auto-open the modal — the user must review the
  // full order details first before deciding to register the activity.
  useEffect(() => {
    if (!rcInboxCtx) return;
    if (rcInboxCtx.kind === 'sms') {
      setActType('SMS RingCentral');
    } else if (rcInboxCtx.kind === 'voicemail') {
      setActType('Voicemail RingCentral');
    }
    if (rcInboxCtx.referenceText) {
      setActComment(rcInboxCtx.referenceText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRealDateChange = (val) => {
    setApptRealDate(val);
    setApptSgDate(val);
    // Reset time when date changes
    setApptRealTime("");
    setApptSgTime("");
  };
  const handleRealTimeChange = (val) => {
    setApptRealTime(val);
    setApptSgTime(REAL_TIME_TO_SG_TIME[val] || "");
  };

  // Select a date+window from the zone capacity panel
  const handleSelectDateWindow = (date, window) => {
    setApptRealDate(date);
    setApptSgDate(date);
    setApptRealTime(window);
    setApptSgTime(REAL_TIME_TO_SG_TIME[window] || "");
  };

  const handleSaveInfo = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await axios.put(`${API}/orders/${orderId}`, { ...contacts, comments });
      const res = await axios.get(`${API}/orders/${orderId}`);
      setOrder(res.data);
      setMessage({ type: "success", text: "Contact & comments saved" });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to save" });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitActivity = async () => {
    if (!actType || !actResult) return;

    // Build base payload (used by both normal flow and Call PH → SMS combo).
    // operator_name is intentionally omitted — backend auto-resolves from the
    // authenticated user (Apr 2026 change; personal logins for everyone).
    const payload = {
      activity_type: actType,
      result: actResult,
      comment: actComment || null,
    };
    // RC Inbox auto-archive hook: when this activity originated from an RC
    // SMS/voicemail item, pass the rc_id so the backend marks the inbox item
    // as ARCHIVED after writing the scheduling_log entry.
    if (rcInboxCtx && rcInboxCtx.rc_id && rcInboxCtx.kind) {
      payload.rc_inbox_kind = rcInboxCtx.kind;
      payload.rc_inbox_rc_id = rcInboxCtx.rc_id;
    }
    if (actType === "Set Appointment") {
      payload.appointment_real_date = apptRealDate;
      payload.appointment_real_time = apptRealTime;
      payload.appointment_sg_date = apptSgDate;
      payload.appointment_contact_name = apptContactName;
      payload.appointment_contact_phone = apptContactPhone;
      const wc = WINDOW_CONFIG[apptRealTime];
      if (wc) {
        payload.internal_start_time = wc.start;
        payload.internal_end_time = wc.end;
      }
      // Safeguard time is AUTO-DERIVED from Internal — never operator-typed.
      // Backend re-derives + validates; we send the label only for display logs.
      if (INTERNAL_TO_SG_LABEL[apptRealTime]) {
        payload.appointment_sg_time = INTERNAL_TO_SG_LABEL[apptRealTime];
      }
    }

    // ── Call PH OR Set Appointment OR SMS → intercept and open the SMS modal ──
    // (Angela 2026-02) SMS activity now also routes through the same modal so
    // operators get the same editable template + zone-availability block.
    const isCallPH = actType === "Call PH";
    const isSetAppt = actType === "Set Appointment";
    const isSms = actType === "SMS";
    if (isCallPH || isSetAppt || isSms) {
      setPendingActivityPayload(payload);
      const clientName = order.cliente || "your insurance company";
      const propertyAddr = [order.address, order.city, order.state].filter(Boolean).join(", ") || order.address || "";
      const orderNum = order.order_number || "";
      const isExternal = (order.flow_type || "").toLowerCase() === "external";

      let smsTemplate;
      if (isSetAppt) {
        // Confirmation SMS for newly-set appointment (Internal flow only — External has no appointments).
        // CRITICAL: use internal AARIFI window — never the Safeguard reported/shifted time.
        const apptDateFmt = apptRealDate ? fmtDate(apptRealDate) : "your scheduled date";
        const wc = WINDOW_CONFIG[apptRealTime]; // internal start/end
        const apptWindowRaw = wc ? `${wc.start} - ${wc.end}` : (apptRealTime || "");
        const apptWindow = toAmPm(apptWindowRaw);
        smsTemplate =
          `Hello, this is AARIFI Inspections, a Safeguard Properties affiliate.\n\n` +
          `Your ${clientName} home inspection at ${propertyAddr} is confirmed for ${apptDateFmt}${apptWindow ? " · " + apptWindow : ""}.\n\n` +
          `Order #: ${orderNum}\n\n` +
          `Please make sure the property is accessible. Reply to this message or call us if you need to reschedule.\n\n` +
          `Thank you.`;
      } else if (isExternal) {
        // External exterior-only inspection notice (NO appointment, NO interior access)
        smsTemplate =
          `Hello, this is AARIFI Field Inspection, an authorized Safeguard Properties contractor for ${clientName}.\n\n` +
          `An exterior-only insurance inspection of your property at:\n\n` +
          `${propertyAddr}\n\n` +
          `will be completed within the next 3 days.\n\n` +
          `No appointment or interior access is required.\n\n` +
          `Please ensure gates are unlocked and pets are secured.\n\n` +
          `Kind regards,\n` +
          `Scheduling Department\n` +
          `AARIFI Field Inspection\n` +
          `Authorized Safeguard Properties Contractor for ${clientName}\n` +
          `(401) 404-6542`;
      } else {
        // Internal follow-up SMS after an attempted Call PH (requesting contact + appointment)
        // (Angela 2026-02) Inject next 2 INTERNAL window slots from the order's zone
        // when available, using the shared `buildAvailabilityLines` helper. If no
        // slots / no zone, fall back to the original template (non-regression).
        // (Angela 2026-02 wording lock) Customer-facing text MUST NOT include
        // client/vendor codes (MAPFRE, UNIMU, CONCOR, etc.). Use generic
        // "home interior and exterior inspection".
        const availLines = buildAvailabilityLines(zoneInfo, 2);
        if (availLines.length > 0) {
          smsTemplate =
            `Hello, this is AARIFI Inspections, a Safeguard Properties affiliate.\n\n` +
            `We just left you a voicemail and we are calling you about your home interior and exterior inspection for:\n\n` +
            `${propertyAddr}\n\n` +
            `Order #: ${orderNum}\n` +
            `Insured/Client: ${order.cliente || "Not available"}\n\n` +
            `For this inspection, both the interior and exterior of the property must be available to our inspector on the scheduled inspection date.\n\n` +
            `We currently have availability in your area on:\n` +
            availLines.map(l => `- ${l}`).join("\n") + `\n\n` +
            `Please let us know which option works best for you.\n\n` +
            `Thank you,\nAARIFI Inspections`;
        } else {
          smsTemplate =
            `Hello, this is AARIFI Inspections, a Safeguard Properties affiliate.\n\n` +
            `We just left you a voicemail and we are calling you about your home interior and exterior inspection for:\n\n` +
            `${propertyAddr}\n\n` +
            `Order #: ${orderNum}\n` +
            `Insured/Client: ${order.cliente || "Not available"}\n\n` +
            `For this inspection, both the interior and exterior of the property must be available to our inspector on the scheduled inspection date.\n\n` +
            `Please reply to this message or return our call so we can schedule a date.\n\n` +
            `Thank you,\nAARIFI Inspections`;
        }
      }
      setSmsText(smsTemplate);
      setPostCallStep("ask_sms");
      setSmsSkipReason("");
      setComboSmsSent(false);
      setComboSmsSkipReason("");
      setComboEmailSent(false);
      setSmsOverridePhone("");
      setShowActivityModal(false);
      setShowPostCallSms(true);
      return;
    }

    // ── Normal flow: just submit ──
    await submitActivityPayload(payload);
  };

  // Submit the pre-built activity payload (shared by normal flow + Call PH combo)
  const submitActivityPayload = async (payload) => {
    setSubmittingActivity(true);
    setMessage(null);
    try {
      await axios.post(`${API}/orders/${orderId}/activity`, payload);
      // Navigate back with a fresh refresh signal so the scheduling queue counters
      // re-fetch immediately (not on the 30s interval).
      navigate('/scheduling', { state: { refreshedAt: Date.now() } });
      return;
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to register activity" });
    } finally {
      setSubmittingActivity(false);
    }
  };

  // Normalize a phone string to +1XXXXXXXXXX if it has 10-11 digits
  const normalizePhoneForSms = (raw) => {
    if (!raw) return null;
    const digits = String(raw).replace(/\D/g, "");
    if (digits.length === 10) return { formatted: `+1${digits}`, display: raw.trim() };
    if (digits.length === 11 && digits.startsWith("1")) return { formatted: `+${digits}`, display: raw.trim() };
    if (digits.length >= 7) return null; // invalid length
    return null;
  };

  // Step 1 (SMS YES): send SMS via RingCentral, then submit activity with sms_sent=true
  const handleSmsSendYes = async () => {
    let phone = null;
    if (smsOverridePhone.trim()) {
      phone = normalizePhoneForSms(smsOverridePhone);
      if (!phone) {
        setMessage({ type: "error", text: "The phone number you typed isn't valid. Use 10 digits (e.g., 6175551234)." });
        return;
      }
    } else {
      phone = getOrderPhone();
    }
    if (!phone) {
      setMessage({ type: "error", text: "No valid phone number. Type an alternative number in the field below." });
      return;
    }
    if (!smsText.trim()) {
      setMessage({ type: "error", text: "SMS text cannot be empty." });
      return;
    }
    setSmsSending(true);
    // STEP 1: Send SMS. If this fails, keep the modal open so the operator
    // can retry or skip — DO NOT close the modal or navigate.
    try {
      await axios.post(`${API}/ringcentral/order-sms`, { to_number: phone.formatted, text: smsText.trim(), order_id: orderId });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to send SMS" });
      setSmsSending(false);
      return;
    }
    // Angela Feb 2026 — post-send UI fix (Scheduling SMS confirm flow):
    // SMS was sent successfully.  From here on, the operator must ALWAYS
    // return to the Scheduling queue, even if the activity-log POST fails
    // (e.g., transient network error).  The SMS is already out and the
    // modal must never stay open after a successful send.
    const payload = {
      ...(pendingActivityPayload || {}),
      sms_sent: true,
      email_sent: !!comboEmailSent,
    };
    try {
      await axios.post(`${API}/orders/${orderId}/activity`, payload);
    } catch (err) {
      // Non-blocking: SMS already sent.  Warn the operator but still
      // close the modal and return to the queue.
      console.error("Activity log failed after SMS sent:", err);
      setMessage({ type: "error", text: "SMS sent, but activity log failed. Please re-register the activity from the queue." });
    } finally {
      setShowPostCallSms(false);
      setPendingActivityPayload(null);
      setSmsSending(false);
      setSubmittingActivity(false);
      navigate('/scheduling', { state: { refreshedAt: Date.now() } });
    }
  };

  // Step 1 (SMS NO): confirm skip reason and submit activity with sms_sent=false.
  // The email combo checkbox is still honored independently.
  const handleSmsSkipConfirm = async () => {
    const payload = {
      ...(pendingActivityPayload || {}),
      sms_sent: false,
      sms_skip_reason: smsSkipReason || null,
      email_sent: !!comboEmailSent,
    };
    setShowPostCallSms(false);
    setPendingActivityPayload(null);
    await submitActivityPayload(payload);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D1A054]" />
        </div>
      </Layout>
    );
  }
  if (!order) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-500">Order not found</p>
          <button onClick={() => navigate(-1)} className="mt-4 text-[#D1A054] hover:text-[#B08A3E]">Go back</button>
        </div>
      </Layout>
    );
  }

  const flowType = order.flow_type || "Regular";
  const isInternal = flowType === "Internal";
  const isExternal = flowType === "External";
  const isRegular = flowType === "Regular";

  const availableActivities = isExternal
    ? EXTERNAL_ACTIVITIES
    : isRegular
    ? ["Call PH", "SMS", "Email", "Voicemail", "Priority", "Send to Admin Review"]
    : ACTIVITY_TYPES;
  const showApptFields = actType === "Set Appointment" && !isExternal && !isRegular;

  const flowColor = isInternal
    ? "bg-[#2F5D8A]/15 text-[#2F5D8A]"
    : isExternal
    ? "bg-[#6B5B8D]/15 text-[#6B5B8D]"
    : "bg-slate-500/20 text-gray-500";

  const apptColor = order.appointment_set === "yes" ? "text-[#6B8F62]" : "text-gray-500";
  const resolvedZoneId = order.zone_id || zoneInfo?.zone_id;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-4">
        {/* ── Header ── */}
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg" data-testid="back-btn">
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-gray-800 truncate">Order {order.order_number}</h1>
            <p className="text-gray-500 text-xs">{order.company} &middot; {order.cliente}</p>
          </div>
          <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase ${flowColor}`}>{flowType}</span>
        </div>

        {/* ── Action buttons ── */}
        <div className="flex flex-wrap gap-2" data-testid="action-bar">
          <button
            onClick={() => setShowActivityModal(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#2F5D8A] hover:bg-[#3B6D9C] border border-[#2F5D8A]/25 rounded-lg text-sm text-white font-semibold transition-colors"
            data-testid="action-register-activity"
          >
            <ClipboardCheck className="h-3.5 w-3.5" />
            <span>Register Activity</span>
          </button>
          <button
            onClick={() => document.getElementById("contact-section")?.scrollIntoView({ behavior: "smooth" })}
            className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg text-sm text-gray-700 transition-colors"
            data-testid="action-edit"
          >
            <Edit className="h-3.5 w-3.5 text-[#B08A3E]" />
            <span>Edit Contact</span>
          </button>
          {/* PRIORITY BUTTONS REMOVED (Angela 2026-04-29):
              Scheduling does not manage Priority Queue from the Order Detail
              anymore. Use the Priority workflow in the Scheduling list/cockpit
              instead — this screen is read-only for routing actions. */}
          {/* CANCEL APPOINTMENT BUTTON REMOVED (Angela 2026-05-09 / B13.A):
              AARIFI Scheduling does not own rescheduling or cancellation as
              an app workflow. Safeguard/CIFG handles real cancellations and
              we re-import the change passively. Any internal inspector ↔
              Scheduling communication about a needed new appointment will
              be added later as INTERNAL-ONLY (no status change, no queue
              change, no appointment-field mutation). */}
          {/* REMOVE ASSIGNMENT BUTTON REMOVED (Angela 2026-04-29):
              Scheduling does NOT manage assignment. Inspector belongs to Dispatch
              only — therefore Scheduling cannot remove an assignment from this
              screen. Use the Dispatch screen if reassignment is needed. */}
        </div>

        {/* ── Message bar ── */}
        {message && (
          <div className={`p-2.5 rounded-lg flex items-center gap-2 text-sm ${message.type === "success" ? "bg-[#A7C4A0]/15 border border-[#A7C4A0]/40 text-[#6B8F62]" : "bg-[#C07070]/12 border border-[#C07070]/30 text-[#8B4545]"}`}>
            {message.type === "success" ? <CheckCircle className="h-4 w-4 shrink-0" /> : <AlertCircle className="h-4 w-4 shrink-0" />}
            <span>{message.text}</span>
          </div>
        )}

        {/* ═══════ 1. ORDER SUMMARY ═══════ */}
        <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="order-summary">
          <SectionTitle>Order Summary</SectionTitle>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-3">
            <SummaryField label="Order #" value={order.order_number} />
            <SummaryField label="Type" value={order.order_type} />
            <SummaryField label="Company" value={order.company} />
            <SummaryField label="Flow" value={flowType} accent={isInternal ? "text-[#2F5D8A]" : isExternal ? "text-[#6B5B8D]" : "text-gray-600"} />
            <SummaryField label="Cliente" value={order.cliente} />
            <SummaryField label="Address" value={order.address} />
            <SummaryField label="City" value={order.city} />
            <SummaryField label="State" value={order.state} />
            <SummaryField label="Due Date" value={formatDateDisplay(order.due_date)} />
            {(() => {
              const sLabel = computeSchedulingStatus(order);
              return (
                <div data-testid="order-summary-scheduling-status">
                  <p className="text-gray-400 text-[10px] uppercase tracking-wider">Status</p>
                  <p
                    className={`text-sm font-bold ${schedulingStatusAccent(sLabel)}`}
                    data-testid="scheduling-status-value"
                  >
                    {sLabel}
                  </p>
                </div>
              );
            })()}
            {/* F3-pré (Angela 2026-05-02) — AARIFI STATUS (canonical,
                read-only). Derived from order.aarifi_scheduling_status
                (backend F2). Never editable. When null (order outside
                Scheduling domain) shows "Not in Scheduling". Sits beside
                the legacy Status on the Order Summary grid. Does NOT
                replace legacy Status. */}
            <div data-testid="order-summary-aarifi-status">
              <p className="text-gray-400 text-[10px] uppercase tracking-wider">AARIFI Status</p>
              {order.aarifi_scheduling_status ? (
                <p
                  className={`text-sm ${aarifiStatusClass(order.aarifi_scheduling_status)}`}
                  data-testid="aarifi-scheduling-status-value"
                >
                  {order.aarifi_scheduling_status}
                </p>
              ) : (
                <p
                  className="text-sm text-gray-400 italic"
                  data-testid="aarifi-scheduling-status-value"
                >
                  Not in Scheduling
                </p>
              )}
            </div>
            {/* QUEUE / OPERATOR / INSPECTOR / EOD UPDATE / APPT SET fields removed
                (Angela 2026-04-29): Scheduling has no inspector, no operator
                routing field, no Dispatch EOD. APPT SET is implicit in the
                Scheduling Status above. */}
          </div>

          {/* Attempts & Activity Counters */}
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              <div className="bg-gray-100 rounded-lg p-2.5 text-center" data-testid="counter-attempts">
                <p className="text-gray-400 text-[9px] uppercase tracking-wider">Attempts (SG)</p>
                <p className="text-lg font-bold text-gray-800">{order.attempts_number ?? order.attempts ?? 0}</p>
              </div>
              <div className="bg-gray-100 rounded-lg p-2.5 text-center" data-testid="counter-ph-calls">
                <p className="text-gray-400 text-[9px] uppercase tracking-wider">PH Calls</p>
                <p className="text-lg font-bold text-[#2F5D8A]">{order.call_count || 0}</p>
              </div>
              <div className="bg-gray-100 rounded-lg p-2.5 text-center" data-testid="counter-agent-calls">
                <p className="text-gray-400 text-[9px] uppercase tracking-wider">Agent Calls</p>
                <p className="text-lg font-bold text-[#2F5D8A]">{order.agent_call_count || 0}</p>
              </div>
              <div className="bg-gray-100 rounded-lg p-2.5 text-center" data-testid="counter-sms">
                <p className="text-gray-400 text-[9px] uppercase tracking-wider">SMS</p>
                <p className="text-lg font-bold text-[#B08A3E]">{order.sms_count || 0}</p>
              </div>
              <div className="bg-gray-100 rounded-lg p-2.5 text-center" data-testid="counter-email">
                <p className="text-gray-400 text-[9px] uppercase tracking-wider">Email</p>
                <p className="text-lg font-bold text-[#6B8F62]">{order.email_count || 0}</p>
              </div>
            </div>
          </div>

          {/* ─── PHASE 0 (Angela 2026-04-29): Internal vs Safeguard times ─── */}
          {/* PHASE 0 Fix B: Internal (AARIFI Real) is the dominant source of truth.
              Safeguard is shown as a smaller, muted reference labelled "Reported by
              Safeguard — NOT used in client SMS".
              STRICT RULE (Angela 2026-04-29): The entire appointment block is
              hidden unless appointment_set === "yes". When no real appointment
              exists, NO placeholder, NO reference time, NO Safeguard window
              and NO yellow warning is rendered. */}
          {order.appointment_set === "yes" && (
            <div className="mt-4 pt-3 border-t border-gray-200 space-y-2" data-testid="appointment-times-block">
              {/* INTERNAL (AARIFI REAL) — DOMINANT */}
              <div
                className={`rounded-lg p-4 border-2 ${
                  order.internal_start_time && order.internal_end_time
                    ? 'bg-[#EAF1F8] border-[#2F5D8A]/60 shadow-sm'
                    : 'bg-yellow-50 border-yellow-400'
                }`}
                data-testid="appointment-internal-block"
              >
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-[#2F5D8A]">
                    Internal Appointment (AARIFI — used for client SMS)
                  </p>
                  {order.internal_start_time && order.internal_end_time && (
                    <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase bg-[#2F5D8A] text-white tracking-wider">
                      Source of Truth
                    </span>
                  )}
                </div>
                {order.internal_start_time && order.internal_end_time ? (
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                    <p className="text-xl font-bold text-[#1F2937]" data-testid="appointment-internal-date">
                      {order.appointment_real_date ? fmtDate(order.appointment_real_date) : '—'}
                    </p>
                    <p className="text-lg font-semibold text-[#2F5D8A]" data-testid="appointment-internal-time">
                      {formatInternalRange(order.internal_start_time, order.internal_end_time)}
                    </p>
                  </div>
                ) : (
                  <p className="text-[13px] font-semibold text-yellow-800 mt-1 flex items-start gap-1.5" data-testid="appointment-internal-warning">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-px" />
                    Internal time missing — SMS will <b className="underline">NOT</b> be sent until an Internal start &amp; end time are set.
                  </p>
                )}
              </div>

              {/* SAFEGUARD (REPORTED) — SECONDARY / MUTED */}
              <div className="rounded-md px-3 py-2 bg-gray-50 border border-gray-200 flex flex-wrap items-baseline gap-x-3 gap-y-0.5" data-testid="appointment-sg-block">
                <p className="text-[9px] uppercase tracking-wider font-semibold text-gray-400">
                  Reported by Safeguard
                </p>
                <p className="text-[12px] text-gray-600" data-testid="appointment-sg-date">
                  {order.appointment_sg_date ? fmtDate(order.appointment_sg_date) : '—'}
                </p>
                <p className="text-[12px] text-gray-600" data-testid="appointment-sg-time">
                  {order.appointment_sg_time ? formatSgTime(order.appointment_sg_time) : '—'}
                </p>
                <p className="text-[10px] italic text-gray-400 ml-auto">
                  Reference only — never sent to client
                </p>
              </div>
            </div>
          )}

          {/* Status flags */}
          <div className="flex gap-2 mt-3">
            {order.urgent_call === "yes" && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#C07070]/20 text-[#8B4545] border border-[#C07070]/30" data-testid="badge-urgent">URGENT</span>
            )}
            {order.admin_review === "yes" && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-yellow-500/20 text-[#9A7A3E] border border-yellow-500/30" data-testid="badge-admin-review">ADMIN REVIEW</span>
            )}
          </div>
        </div>

        {/* ═══════ CONTACT ACTIONS (Phase 1 — Call only) ═══════ */}
        <div className="flex flex-wrap gap-2" data-testid="contact-actions-bar">
          <button
            type="button"
            onClick={async () => {
              const phone = getOrderPhone();
              if (!phone) {
                setShowManualPhone(true);
                setMessage({ type: 'error', text: 'PH phone is missing or invalid (must be US 10-digit). Please enter a valid phone number before calling.' });
                return;
              }
              if (!window.confirm(`Call ${phone.display}?\n\nRingCentral will ring YOUR phone first (the one in your Profile → RingCentral Phone), then bridge to the client.`)) return;
              setActType("Call PH");
              setShowActivityModal(true);
              setCallLoading(true);
              try {
                await axios.post(`${API}/ringcentral/ring-out`, { to_number: phone.formatted, order_id: orderId });
                setMessage({ type: 'success', text: `Call initiated to ${phone.display}. Your phone should ring shortly — answer to bridge to the client.` });
              } catch (e) {
                // Surface the exact backend reason — most commonly:
                //  - "Your RingCentral phone is not configured. Open Profile..."
                //  - "Client phone is invalid..."
                const backendDetail = e?.response?.data?.detail;
                setMessage({
                  type: 'error',
                  text: backendDetail || `Call failed (${e?.response?.status || 'network error'}). Please try again.`,
                });
              }
              setCallLoading(false);
            }}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#2F5D8A] hover:bg-[#3B6D9C] border border-[#2F5D8A]/25 rounded-lg text-sm text-white font-semibold transition-colors"
            data-testid="contact-action-call"
          >
            <Phone className="h-3.5 w-3.5" />
            <span>Call</span>
          </button>
        </div>

        {/* ═══════ 1.5 ZONE SCHEDULING BLOCK (Internal only) ═══════ */}
        {isInternal && (
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="zone-scheduling-block">
            <div className="flex items-center gap-1.5 mb-3">
              <MapPin className="h-4 w-4 text-[#2F5D8A]" />
              <SectionTitle>Zone & Scheduling</SectionTitle>
              {loadingZone && <div className="ml-2 animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-[#2F5D8A]" />}
            </div>

            {/* Zone header info */}
            <div className="flex items-center gap-4 mb-3">
              <div>
                <p className="text-gray-400 text-[9px] uppercase tracking-wider">City</p>
                <p className="text-sm font-medium text-gray-700" data-testid="zone-city">{order.city || '-'}</p>
              </div>
              <div>
                <p className="text-gray-400 text-[9px] uppercase tracking-wider">Zone</p>
                <p className="text-sm font-medium text-[#2F5D8A]" data-testid="zone-name">{resolvedZoneId || '-'}</p>
              </div>
            </div>

            {/* Zone not mapped warning */}
            {zoneNotMapped && !loadingZone && (
              <div className="bg-[#D1A054]/10 border border-[#D1A054]/30 rounded-lg p-3 flex items-start gap-2" data-testid="zone-not-mapped">
                <AlertTriangle className="h-4 w-4 text-[#B08A3E] shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-[#B08A3E]">Zone not mapped</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    The city "{order.city}" is not assigned to any zone yet. Go to <a href="/zone-calendar" className="text-[#2F5D8A] underline hover:text-[#2F5D8A]">Zone Calendar</a> to add this city to a zone.
                  </p>
                </div>
              </div>
            )}

            {/* Next 2 dates with capacity */}
            {zoneInfo?.dates?.length > 0 && (
              <div className="space-y-2" data-testid="zone-dates-capacity">
                {zoneInfo.dates.map((dateEntry) => (
                  <div key={dateEntry.date} className="bg-gray-50/50 rounded-lg border border-gray-200 p-3" data-testid={`zone-date-${dateEntry.date}`}>
                    <div className="flex items-center gap-1.5 mb-2">
                      <Calendar className="h-3 w-3 text-[#B08A3E]" />
                      <span className="text-xs font-semibold text-[#1F2937]">{fmtDate(dateEntry.date)}</span>
                      <span className="text-[10px] text-gray-400">{dateEntry.date}</span>
                    </div>
                    <div className="grid grid-cols-5 gap-1.5">
                      {dateEntry.windows.map((w) => {
                        const isFull = w.full;
                        const isSelected = apptRealDate === dateEntry.date && apptRealTime === w.window;
                        return (
                          <button
                            key={w.window}
                            type="button"
                            disabled={isFull}
                            onClick={() => handleSelectDateWindow(dateEntry.date, w.window)}
                            data-testid={`slot-${dateEntry.date}-${w.window.replace(/:/g, '')}`}
                            className={`rounded-lg border px-2 py-1.5 text-center transition-all ${
                              isFull
                                ? 'bg-[#F5EDED]0/5 border-red-500/20 cursor-not-allowed opacity-60'
                                : isSelected
                                ? 'bg-[#D1A054]/15 border-[#D1A054]/50 ring-1 ring-[#D1A054]/40'
                                : 'bg-white/60 border-gray-300/40 hover:border-gray-300 cursor-pointer'
                            }`}
                          >
                            <p className={`text-[10px] font-medium ${isFull ? 'text-[#8B4545]' : isSelected ? 'text-[#9A7A3E]' : 'text-gray-600'}`}>
                              {w.window}
                            </p>
                            <p className={`text-xs font-bold mt-0.5 ${
                              isFull ? 'text-[#8B4545]' : w.booked > 0 ? 'text-[#B08A3E]' : 'text-[#6B8F62]'
                            }`}>
                              {w.booked}/{w.max_capacity}
                            </p>
                            {isFull && (
                              <p className="text-[8px] text-[#8B4545] font-semibold mt-0.5" data-testid={`full-${dateEntry.date}-${w.window.replace(/:/g, '')}`}>FULL</p>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* No dates available */}
            {!loadingZone && resolvedZoneId && zoneInfo?.dates?.length === 0 && (
              <p className="text-gray-400 text-xs py-2" data-testid="no-zone-dates">No upcoming scheduled dates for this zone.</p>
            )}
          </div>
        )}

        {/* ═══════ 1.7 COMPANY TRAINING / RULES ═══════ */}
        {order.cliente && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid="training-block">
            <button
              onClick={() => setTrainingOpen(!trainingOpen)}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
              data-testid="training-toggle"
            >
              <div className="flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-[#B08A3E]" />
                <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest">
                  Company Rules — {order.cliente}
                </span>
                {training && (
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#A7C4A0]/20 text-[#6B8F62]">FOUND</span>
                )}
                {!training && order.cliente && (
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-gray-200 text-gray-500">NOT FOUND</span>
                )}
              </div>
              {trainingOpen ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
            </button>
            {trainingOpen && (
              <div className="px-4 pb-4 border-t border-gray-200 pt-3">
                {training ? (
                  <div className="space-y-3" data-testid="training-content">
                    {[
                      ["Inspection Type", training.inspection_type],
                      ["Scheduling Process", training.scheduling_process],
                      ["Calling Cadence", training.calling_cadence],
                      ["No Show Rules", training.no_show],
                      ["Unable to Complete", training.unable_to_complete],
                      ["Business Rules", training.business_rules],
                    ].filter(([, v]) => v).map(([label, value]) => (
                      <div key={label}>
                        <p className="text-[10px] font-semibold text-[#B08A3E] uppercase tracking-wider mb-0.5">{label}</p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{value}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm py-2" data-testid="training-not-found">
                    Training not found for "{order.cliente}"
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* ═══════ 2. CONTACT INFORMATION ═══════ */}
        <div className="bg-white rounded-lg border border-gray-200 p-4" id="contact-section" data-testid="contact-info">
          <SectionTitle>Contact Information</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { key: "ph_name", label: "PH Name" },
              { key: "ph_phone", label: "PH Phone" },
              { key: "ph_email", label: "PH Email" },
              { key: "agt_name", label: "Agent Name" },
              { key: "agt_phone", label: "Agent Phone" },
              { key: "agt_email", label: "Agent Email" },
            ].map((f) => (
              <div key={f.key}>
                <label className={labelCls}>{f.label}</label>
                <input
                  type={f.key.includes("email") ? "email" : "text"}
                  value={contacts[f.key] || ""}
                  onChange={(e) => setContacts((c) => ({ ...c, [f.key]: e.target.value }))}
                  className={inputCls}
                  data-testid={`contact-${f.key}`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* ═══════ 2.5 IMPORTED COMMENT (SAFEGUARD) ═══════ */}
        {order && order.safeguard_comment && (order.flow_type === "Internal" || order.flow_type === "External") && (
          <div className="bg-white rounded-lg border border-[#D1A054]/20 p-4" data-testid="safeguard-comment-section">
            <SectionTitle>Imported Comment (Safeguard)</SectionTitle>
            <div className="bg-gray-50/50 rounded-lg border border-gray-200 p-3">
              <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed" data-testid="safeguard-comment-text">{order.safeguard_comment}</p>
            </div>
          </div>
        )}

        {/* ═══════ 3. REGISTER ACTIVITY MODAL ═══════ */}
        {showActivityModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowActivityModal(false)}>
            <div className="bg-white rounded-xl border border-gray-200 p-5 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="register-activity-modal">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-bold text-[#1F2937] flex items-center gap-2">
                  <ClipboardCheck className="h-4 w-4 text-[#2F5D8A]" />
                  Register Activity
                </h2>
                <button onClick={() => setShowActivityModal(false)} className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-[#1F2937]" data-testid="close-activity-modal">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-[#2F5D8A]/8 border border-[#2F5D8A]/20" data-testid="registered-as-badge">
                <UserCircle2 className="h-4 w-4 text-[#2F5D8A] shrink-0" />
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-wider text-[#2F5D8A]/80 font-semibold leading-none mb-0.5">Registered as</p>
                  <p className="text-sm font-bold text-[#2F5D8A] leading-tight">{authUser?.name || authUser?.email || "your login"}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Activity Type */}
                  <div>
                    <label className={labelCls}>Activity Type *</label>
                    <select value={actType} onChange={(e) => setActType(e.target.value)} className={selectCls} data-testid="activity-type">
                      <option value="">Select activity...</option>
                      {availableActivities.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  </div>
                  {/* Result */}
                  <div>
                    <label className={labelCls}>Result *</label>
                    <select value={actResult} onChange={(e) => setActResult(e.target.value)} className={selectCls} data-testid="activity-result">
                      <option value="">Select result...</option>
                      {RESULTS.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Comment */}
                <div>
                  <label className={labelCls}>Comment</label>
                  <input type="text" value={actComment} onChange={(e) => setActComment(e.target.value)} className={inputCls} placeholder="Optional note..." data-testid="activity-comment" />
                </div>

                {/* ── Appointment sub-form (Internal + Set Appointment) ── */}
                {showApptFields && (
                  <div className="border border-[#D1A054]/30 rounded-lg p-3 bg-[#D1A054]/5 space-y-3" data-testid="appointment-details-section">
                    <p className="text-[#B08A3E] text-[10px] uppercase font-semibold tracking-wider">Appointment Details</p>

                    {/* Selected slot display */}
                    {apptRealDate && apptRealTime && (
                      <div className="space-y-1.5" data-testid="selected-slot-display">
                        <div className="bg-[#A7C4A0]/15 border border-[#A7C4A0]/40 rounded-lg px-3 py-2 flex items-center gap-2">
                          <Clock className="h-3.5 w-3.5 text-[#6B8F62] shrink-0" />
                          <span className="text-[10px] text-[#6B8F62] font-semibold uppercase tracking-wider">Internal Window</span>
                          <span className="text-sm text-[#6B8F62] font-medium ml-auto">
                            {fmtDate(apptRealDate)} &middot; {WINDOW_CONFIG[apptRealTime] ? `${WINDOW_CONFIG[apptRealTime].start} – ${WINDOW_CONFIG[apptRealTime].end}` : apptRealTime}
                          </span>
                        </div>
                        <div className="bg-[#2F5D8A]/8 border border-[#2F5D8A]/25 rounded-lg px-3 py-2 flex items-center gap-2">
                          <Lock className="h-3.5 w-3.5 text-[#2F5D8A] shrink-0" />
                          <span className="text-[10px] text-[#2F5D8A] font-semibold uppercase tracking-wider">Reported to Safeguard</span>
                          <span className="text-sm text-[#2F5D8A] font-bold ml-auto" data-testid="sg-reported-time">
                            {fmtDate(apptSgDate)} &middot; {apptSgTime}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Manual date/time entry if no zone or user wants to override */}
                    {(!resolvedZoneId || zoneNotMapped) && (
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className={labelCls}>Real Date</label>
                            <input type="date" value={apptRealDate} onChange={(e) => handleRealDateChange(e.target.value)} className={inputCls} data-testid="appt-real-date" />
                          </div>
                          <div>
                            <label className={labelCls}>Real Time</label>
                            <select value={apptRealTime} onChange={(e) => handleRealTimeChange(e.target.value)} className={selectCls} data-testid="appt-real-time">
                              <option value="">Select time range...</option>
                              {REAL_TIME_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className={labelCls}>SG Date (auto)</label>
                            <input type="date" value={apptSgDate} readOnly className={`${inputCls} opacity-70 border-[#2F5D8A]/25`} data-testid="appt-sg-date" />
                          </div>
                          <div>
                            <label className={labelCls}>SG Reported Time (auto)</label>
                            <input type="text" value={apptSgTime} readOnly className={`${inputCls} opacity-70 border-[#2F5D8A]/25`} data-testid="appt-sg-time" />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Hint: slot was picked from zone block above */}
                    {resolvedZoneId && !zoneNotMapped && !apptRealDate && (
                      <p className="text-gray-500 text-xs" data-testid="pick-slot-hint">
                        Select a date and window from the Zone & Scheduling block above, or enter manually below.
                      </p>
                    )}

                    {/* Manual override link for zoned orders */}
                    {resolvedZoneId && !zoneNotMapped && (
                      <details className="text-xs">
                        <summary className="text-gray-400 cursor-pointer hover:text-gray-500">Manual date/time entry</summary>
                        <div className="mt-2 space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className={labelCls}>Real Date</label>
                              <input type="date" value={apptRealDate} onChange={(e) => handleRealDateChange(e.target.value)} className={inputCls} data-testid="appt-real-date-manual" />
                            </div>
                            <div>
                              <label className={labelCls}>Real Time</label>
                              <select value={apptRealTime} onChange={(e) => handleRealTimeChange(e.target.value)} className={selectCls} data-testid="appt-real-time-manual">
                                <option value="">Select...</option>
                                {REAL_TIME_OPTIONS.map((opt) => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className={labelCls}>SG Date (auto)</label>
                              <input type="date" value={apptSgDate} readOnly className={`${inputCls} opacity-70 border-[#2F5D8A]/25`} />
                            </div>
                            <div>
                              <label className={labelCls}>SG Reported Time (auto)</label>
                              <input type="text" value={apptSgTime} readOnly className={`${inputCls} opacity-70 border-[#2F5D8A]/25`} />
                            </div>
                          </div>
                        </div>
                      </details>
                    )}

                    {/* ── Contact at Property (Required) ── */}
                    <div className="border-t border-[#D1A054]/20 pt-3 mt-1">
                      <p className="text-[#B08A3E] text-[10px] uppercase font-semibold tracking-wider mb-2">Who will be at the property?</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className={labelCls}>Contact Name *</label>
                          <input
                            type="text"
                            value={apptContactName}
                            onChange={(e) => setApptContactName(e.target.value)}
                            placeholder="Name of person at property"
                            className={inputCls}
                            required
                            data-testid="appt-contact-name"
                          />
                        </div>
                        <div>
                          <label className={labelCls}>Contact Phone *</label>
                          <input
                            type="tel"
                            value={apptContactPhone}
                            onChange={(e) => setApptContactPhone(e.target.value)}
                            placeholder="Phone for inspector SMS"
                            className={inputCls}
                            required
                            data-testid="appt-contact-phone"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Reschedule sub-form removed (Angela 2026-05-09): no
                    operational reschedule workflow exists in the app. */}

                <button
                  onClick={handleSubmitActivity}
                  disabled={submittingActivity || !actType || !actResult || (showApptFields && (!apptContactName || !apptContactPhone))}
                  className="w-full py-2.5 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white font-semibold rounded-lg disabled:opacity-40 transition-colors text-sm"
                  data-testid="submit-activity-btn"
                >
                  {submittingActivity ? "Registering..." : "Register Activity"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── POST-CALL / POST-APPOINTMENT SMS Popup ── */}
        {showPostCallSms && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1200] p-4" data-testid="post-call-sms-modal">
            <div className="bg-white rounded-xl border border-gray-200 p-5 w-full max-w-lg shadow-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-bold text-[#1F2937] flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-[#2F5D8A]" />
                  {actType === "Set Appointment"
                    ? "Appointment Set — send confirmation SMS?"
                    : actType === "SMS"
                      ? "Send SMS to client"
                      : "Follow-up after Call PH"}
                </h2>
              </div>

              {/* ── STEP 1: Ask SMS ── */}
              {postCallStep === "ask_sms" && (
                <>
                  <p className="text-xs text-gray-500 mb-3">Would you like to send an SMS now?</p>
                  <div className="mb-3">
                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">SMS Text (pre-filled · editable)</label>
                    <textarea
                      value={smsText}
                      onChange={(e) => setSmsText(e.target.value)}
                      rows={9}
                      className="w-full px-2.5 py-2 border border-gray-300 rounded text-xs font-mono resize-y"
                      data-testid="post-call-sms-text"
                    />
                    <p className="text-[9px] text-gray-400 mt-1">
                      To (on file): {getOrderPhone()?.display || <span className="text-red-500">no valid phone on order</span>}
                    </p>
                  </div>

                  {/* Alternative phone number (optional — if operator wants to send to a different number) */}
                  <div className="mb-3">
                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                      Send to a different number? (optional)
                    </label>
                    <input
                      type="tel"
                      value={smsOverridePhone}
                      onChange={(e) => setSmsOverridePhone(e.target.value)}
                      placeholder="e.g., 617-555-1234 (leave empty to use the number on file)"
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs"
                      data-testid="post-call-sms-override-phone"
                    />
                    {smsOverridePhone.trim() && (
                      <p className="text-[9px] text-[#2F5D8A] mt-1 font-semibold">
                        Will send to: {smsOverridePhone.trim()}
                      </p>
                    )}
                  </div>

                  {/* Email also sent — multi-count combo (Apr 2026).
                      One registration logs Call PH + optional SMS + optional Email. */}
                  <label className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-[#A7C4A0]/10 border border-[#A7C4A0]/30 cursor-pointer hover:bg-[#A7C4A0]/15 transition-colors" data-testid="combo-email-wrapper">
                    <input
                      type="checkbox"
                      checked={comboEmailSent}
                      onChange={(e) => setComboEmailSent(e.target.checked)}
                      className="h-4 w-4 accent-[#6B8F62]"
                      data-testid="combo-email-checkbox"
                    />
                    <Mail className="h-3.5 w-3.5 text-[#6B8F62]" />
                    <span className="text-xs text-[#4A6B42] font-semibold">I also sent an Email for this order</span>
                    <span className="ml-auto text-[9px] text-[#6B8F62] uppercase tracking-wider">+1 Email count</span>
                  </label>

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setPostCallStep("sms_skip_reason")}
                      disabled={smsSending || submittingActivity}
                      className="py-2 bg-gray-100 hover:bg-gray-200 text-sm font-semibold rounded text-gray-700 disabled:opacity-50"
                      data-testid="post-call-sms-no"
                    >
                      No, skip SMS
                    </button>
                    <button
                      onClick={handleSmsSendYes}
                      disabled={smsSending || submittingActivity || !smsText.trim() || (!getOrderPhone() && !smsOverridePhone.trim())}
                      className="py-2 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-sm font-bold rounded disabled:opacity-50"
                      data-testid="post-call-sms-yes"
                    >
                      {smsSending ? "Sending..." : "Yes, send SMS"}
                    </button>
                  </div>
                </>
              )}

              {/* ── STEP 1b: SMS skip reason ── */}
              {postCallStep === "sms_skip_reason" && (
                <>
                  <p className="text-xs text-gray-500 mb-3">Why skip SMS? (optional)</p>
                  <select
                    value={smsSkipReason}
                    onChange={(e) => setSmsSkipReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm mb-3"
                    data-testid="post-call-sms-skip-reason"
                  >
                    <option value="">No reason (skip anyway)</option>
                    <option value="landline">Landline</option>
                    <option value="invalid_mobile">Invalid mobile</option>
                    <option value="send_later">Send later</option>
                    <option value="no_consent">No consent</option>
                    <option value="other">Other</option>
                  </select>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setPostCallStep("ask_sms")}
                      className="py-2 bg-gray-100 hover:bg-gray-200 text-sm font-semibold rounded text-gray-700"
                      data-testid="post-call-sms-back"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleSmsSkipConfirm}
                      disabled={submittingActivity}
                      className="py-2 bg-[#6B8F62] hover:bg-[#5B7F52] text-white text-sm font-bold rounded disabled:opacity-50"
                      data-testid="post-call-sms-confirm-skip"
                    >
                      {submittingActivity ? "Saving..." : (actType === "Set Appointment" ? "Save appointment" : actType === "SMS" ? "Save SMS" : "Save call")}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}


        {/* ═══════ 4. COMMENTS ═══════ */}
        <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="comments-section">
          <SectionTitle>Comments</SectionTitle>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={4}
            className={`${inputCls} min-h-[100px] resize-y`}
            placeholder="Add scheduling notes..."
            data-testid="edit-comments"
          />
        </div>

        {/* ── Save contacts + comments button ── */}
        <button
          onClick={handleSaveInfo}
          disabled={saving}
          className="w-full py-2.5 bg-[#D1A054] hover:bg-[#B08A3E] text-[#162d4a] font-semibold rounded-lg disabled:opacity-50 transition-colors text-sm"
          data-testid="save-info-btn"
        >
          {saving ? "Saving..." : "Save Contact & Comments"}
        </button>

        {/* ── Send to Dispatch button (Admin/Office only) ── */}
        {['Admin', 'Finance', 'Scheduling', 'Operations', 'Operator'].includes(userRole) && userRole !== 'Inspector' && (
          <SendToDispatchButton order={order} onSuccess={(updatedOrder) => {
            setOrder(updatedOrder);
            setMessage({ type: "success", text: `Order sent to Dispatch (was: ${order.status})` });
          }} onError={(msg) => setMessage({ type: "error", text: msg })} />
        )}

        {/* ═══════ 5. SCHEDULING HISTORY ═══════ */}
        <div className="bg-white rounded-lg border border-gray-200" data-testid="scheduling-history">
          <button
            onClick={() => setHistoryOpen(!historyOpen)}
            className="w-full flex items-center justify-between p-4 text-left"
          >
            <SectionTitle>Scheduling History ({activityLog.length})</SectionTitle>
            {historyOpen ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
          </button>

          {historyOpen && (
            <div className="px-4 pb-4">
              {activityLog.length === 0 ? (
                <p className="text-gray-400 text-sm py-2">No activity recorded yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="history-table">
                    <thead>
                      <tr className="border-b border-gray-200 text-gray-500">
                        <th className="text-left py-2 pr-3 font-medium">Date</th>
                        <th className="text-left py-2 pr-3 font-medium">Operator</th>
                        <th className="text-left py-2 pr-3 font-medium">Activity</th>
                        <th className="text-left py-2 pr-3 font-medium">Result</th>
                        <th className="text-left py-2 pr-3 font-medium">Comment</th>
                        <th className="text-left py-2 font-medium">Next Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {activityLog.map((log, i) => (
                        <tr key={i} className="text-gray-600">
                          <td className="py-1.5 pr-3 whitespace-nowrap">{log.action_date ? new Date(log.action_date).toLocaleDateString() : "-"}</td>
                          <td className="py-1.5 pr-3 whitespace-nowrap">{log.operator_name || "-"}</td>
                          <td className="py-1.5 pr-3 whitespace-nowrap">{log.activity_type || "-"}</td>
                          <td className="py-1.5 pr-3 whitespace-nowrap">{log.result || "-"}</td>
                          <td className="py-1.5 pr-3 max-w-[200px] truncate">{log.comment || "-"}</td>
                          <td className="py-1.5 whitespace-nowrap">{log.next_action_date || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ═══════ 5b. INSPECTOR FIELD UPDATES (Angela 2026-05-10) ═══════
            Collapsible card that lists ONLY inspector-side communications
            (Arrived · Finished · Reschedule / Cancel Issue · Denied
            Inspection · Wrong Address). Lives inside the Scheduling order
            screen so the regular Scheduling History above stays clean for
            normal scheduling activities. Read-only; no order mutation. */}
        <div className="bg-white rounded-lg border border-gray-200" data-testid="inspector-field-updates-section">
          <button
            onClick={() => setFieldUpdatesOpen(!fieldUpdatesOpen)}
            className="w-full flex items-center justify-between p-4 text-left"
            data-testid="inspector-field-updates-toggle"
          >
            <SectionTitle>Inspector Field Updates ({fieldUpdates.length})</SectionTitle>
            {fieldUpdatesOpen ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
          </button>

          {fieldUpdatesOpen && (
            <div className="px-4 pb-4">
              {fieldUpdates.length === 0 ? (
                <p className="text-gray-400 text-sm py-2">No inspector field updates yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="inspector-field-updates-table">
                    <thead>
                      <tr className="border-b border-gray-200 text-gray-500">
                        <th className="text-left py-2 pr-3 font-medium">Date / Time</th>
                        <th className="text-left py-2 pr-3 font-medium">Inspector</th>
                        <th className="text-left py-2 pr-3 font-medium">Activity</th>
                        <th className="text-left py-2 font-medium">Comment / Observation</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {fieldUpdates.map((ev, i) => (
                        <tr key={i} className="text-gray-700" data-testid={`inspector-field-update-row-${i}`}>
                          <td className="py-1.5 pr-3 whitespace-nowrap">{ev.created_at ? new Date(ev.created_at).toLocaleString() : "-"}</td>
                          <td className="py-1.5 pr-3 whitespace-nowrap">{ev.operator_name || "-"}</td>
                          <td className="py-1.5 pr-3 whitespace-nowrap font-medium">{ev.activity_type || "-"}</td>
                          <td className="py-1.5 pr-3 max-w-[420px]">{ev.comment || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
        {order.company === 'National' && ['Inspector', 'Dispatch', 'Finance', 'Admin', 'Scheduling'].includes(userRole) && (
          <NationalPhotosPanel orderId={orderId} userRole={userRole} />
        )}

        {/* ═══════ CLIENT PHOTO REQUEST — REMOVED (Angela 2026-05-10).
            Out of approved scope; the panel is no longer rendered on the
            Scheduling order detail. Component file kept on disk. ═══════ */}

        {/* ═══════ EXTERNAL MORNING CALL (External orders only) ═══════
            Apr 2026: same component used on /order/:id (App.js OrderDetailPage).
            Same endpoint, same conditions, same button text. */}
        {order.flow_type === 'External' && ['Admin', 'Scheduling', 'Operator', 'Finance', 'Dispatch', 'Operations'].includes(userRole) && (
          <ExternalMorningCallSection
            order={order}
            orderId={orderId}
            onChange={(updates) => setOrder(prev => ({ ...prev, ...updates }))}
          />
        )}

        {/* ═══════ LOCATION MAP (Admin/Scheduling only) ═══════ */}
        {['Admin', 'Scheduling'].includes(userRole) && (
          <OrderMapSection orderId={orderId} />
        )}

        {/* B13.A (Angela 2026-05-09): Cancel Appointment modal REMOVED. */}
      </div>
    </Layout>
  );
};

export default SchedulingOrderDetailPage;
